import LoadingModal from "../components/modals/LoadingModal";
import NotificationSnackbar from "../components/NotificationSnackbar";
import DataTable from "../components/DataTable";
import InvokeGenerationApi from "../components/InvokeGenerationApi";
import SpecialButton from "../components/SpecialButton";
import AdditionalActions from "../components/AdditonalActions";
import { post } from "@aws-amplify/api";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { apiClient } from "../helpers/apiClient";
import { useAuthInfo } from "../helpers/useAuthInfo";
import { onUpdateEvaluation } from "../graphql/subscriptions";
import {
  Box,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import {
  CGI_RED,
  CGI_SUCCESS,
  CGI_PURPLE,
  congnitoGroups,
  CGI_WARNING,
  CGI_ERROR,
} from "../helpers/enums";
import {
  fetchAllAssessmentAssignmentsByCustomer,
  fetchAllEvaluations,
  fetchAssessmentById,
  fetchCustomersAll,
  fetchCustomersByOwnerGroups,
  fetchPerformedAssessmentByAssessmentAssignment,
} from "../helpers/apiRequests";

export default function AssessmentsEvaluations() {
  const { t, i18n } = useTranslation();
  const { userGroups, ready } = useAuthInfo();

  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [openLoading, setOpenLoading] = useState(false);
  const [customerAssessments, setCustomerAssessments] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [selectedAssessmentAssignmentId, setSelectedAssessmentAssignmentId] =
    useState("");

  const hiddenHeaders = ["id"];
  const exclude = [
    "customerID",
    "performAssessmentID",
    "assessmentID",
    "package",
  ];
  const smallerFields = ["language", "version", "type"];
  const biggerFields = [];
  const specialFields = ["language", "state"];

  const headers =
    customerAssessments?.length > 0
      ? [
          ...Object.keys(customerAssessments[0])
            .filter((key) => !exclude.includes(key))
            .map((key) => {
              return {
                field: key,
                headerName: t(key),
                flex: smallerFields.includes(key)
                  ? 1
                  : biggerFields.includes(key)
                    ? 3
                    : 2,
                hide: hiddenHeaders.includes(key),
                renderCell: (params) => {
                  const { value, field } = params;
                  if (specialFields.includes(field)) {
                    if (field === "language") {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            height: "100%",
                          }}
                        >
                          <Typography
                            sx={{
                              border: 2,
                              borderColor: CGI_PURPLE,
                              borderRadius: 5,
                              px: 0.7,
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      );
                    } else {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            height: "100%",
                          }}
                        >
                          <Typography
                            sx={{
                              border: 2,
                              borderColor:
                                value === "NEW"
                                  ? CGI_RED
                                  : value === "IN_PROGRESS"
                                    ? CGI_WARNING
                                    : CGI_SUCCESS,
                              borderRadius: 5,
                              px: 0.7,
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      );
                    }
                  }
                  return <span>{value}</span>;
                },
              };
            }),
          {
            field: "perform",
            headerName: t("performAssessment"),
            flex: 3,
            sortable: false,
            renderCell: ({ row }) => {
              const id = row.id;
              const assessmentAssignemt = row;
              const assessment = { type: assessmentAssignemt.type };
              return (
                <>
                  <SpecialButton
                    buttontext={t("performAssessment")}
                    routingPath={"performAssessment"}
                    id={id}
                  />
                  {
                    <AdditionalActions
                      assessment={assessment}
                      setNotificationMessage={setNotificationMessage}
                      setOpenNotification={setOpenNotification}
                      setOpenLoading={setOpenLoading}
                      fetchData={fetchCustomerAndAssessments}
                    />
                  }
                </>
              );
            },
          },
          {
            field: "evaluate",
            headerName: t("evaluate"),
            flex: 3,
            sortable: false,
            renderCell: (params) => {
              const { row } = params;
              const assessmentAssignment = row;
              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                    height: "100%",
                  }}
                >
                  <InvokeGenerationApi
                    invokeGenerationApi={invokeGenerationApi}
                    performedAssessmentId={
                      assessmentAssignment.performAssessmentID
                    }
                    assessmentAssignmentId={assessmentAssignment.id}
                    assessmentId={assessmentAssignment.assessmentID}
                    assesmentPackage={assessmentAssignment.package}
                    isNew={
                      assessmentAssignment.state !== "IN_PROGRESS" &&
                      assessmentAssignment.state !== "COMPLETED"
                    }
                  />
                </Box>
              );
            },
          },
        ]
      : [];

  const rows = customerAssessments?.map((row, index) => ({
    id: row.id ?? index,
    ...row,
  }));

  const handleOnRowSelectionChanged = (selectionModel) => {
    if (selectionModel?.ids) {
      setSelectedAssessmentAssignmentId(
        Array.from(selectionModel.ids)[0] || "",
      );
      return;
    }

    if (Array.isArray(selectionModel)) {
      setSelectedAssessmentAssignmentId(selectionModel[0] || "");
      return;
    }

    setSelectedAssessmentAssignmentId(selectionModel || "");
  };

  const evaluationColumns = [
    {
      field: "evaluationFrom",
      headerName: t("evaluationFrom"),
      flex: 3,
    },
    {
      field: "language",
      headerName: t("language"),
      flex: 1,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            height: "100%",
          }}
        >
          <Typography
            sx={{
              border: 2,
              borderColor: CGI_PURPLE,
              borderRadius: 5,
              px: 0.7,
            }}
          >
            {value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "id",
      headerName: "ID",
      flex: 3,
    },
    {
      field: "state",
      headerName: t("state"),
      flex: 1,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              border: 2,
              borderRadius: 20,
              alignItems: "center",
              justifyContent: "center",
              width: "fit-content",
              px: 0.7,
              color:
                value === "RUNNING"
                  ? CGI_WARNING
                  : value === "FINISHED"
                    ? CGI_SUCCESS
                    : CGI_ERROR,
            }}
          >
            {value === "RUNNING" && (
              <CircularProgress size={15} sx={{ mr: 1 }} />
            )}
            <Typography
              sx={{
                fontWeight: 500,
                userSelect: "none",
                textAlign: "center",
              }}
            >
              {value}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "show",
      headerName: "",
      flex: 2,
      sortable: false,
      renderCell: ({ row }) => (
        <SpecialButton
          buttontext={t("goToEvaluation")}
          routingPath={"showEvaluation"}
          id={row.id}
          disabled={row.state !== "FINISHED"}
        />
      ),
    },
  ];

  const filteredEvaluations = selectedAssessmentAssignmentId
    ? evaluations.filter(
        (evaluation) =>
          evaluation.assessmentAssignmentID === selectedAssessmentAssignmentId,
      )
    : evaluations;

  const evaluationsByAssessment = filteredEvaluations.reduce(
    (groups, evaluation) => {
      const assessmentKey = evaluation.assessmentAssignmentID || "unknown";
      const existing = groups[assessmentKey] || {
        assessmentAssignmentID: assessmentKey,
        assessmentName: evaluation.assessmentName,
        customerName: evaluation.customerName,
        rows: [],
      };

      existing.rows.push(evaluation);
      groups[assessmentKey] = existing;
      return groups;
    },
    {},
  );

  const evaluationGroups = Object.values(evaluationsByAssessment).sort((a, b) =>
    (a.assessmentName || "").localeCompare(b.assessmentName || ""),
  );

  function mapEvaluationRow(evaluation, assignment) {
    const infoText =
      t("evaluationFrom") +
      " " +
      new Date(evaluation.createdAt).toLocaleString(getDateLocale(), {
        timeZone: "Europe/Berlin",
      });

    return {
      id: evaluation.id,
      assessmentAssignmentID: evaluation.assessmentAssignmentID,
      createdAt: evaluation.createdAt,
      customerName: assignment?.customerName ?? "",
      assessmentName:
        assignment?.assessmentName ??
        evaluation.assessmentAssignmentID ??
        "Unknown assessment",
      evaluationFrom: infoText,
      language: evaluation.language,
      state: evaluation.state,
    };
  }

  function getDateLocale() {
    const translatedLocale = t("timeSettings");
    if (translatedLocale && translatedLocale !== "timeSettings") {
      return translatedLocale;
    }

    if (i18n.resolvedLanguage?.toLowerCase().startsWith("de")) {
      return "de-DE";
    }

    if (i18n.resolvedLanguage?.toLowerCase().startsWith("fr")) {
      return "fr-FR";
    }

    return "en-US";
  }

  async function fetchAllVisibleEvaluations(
    assignments = customerAssessments,
    { notifyOnError = true } = {},
  ) {
    try {
      const assignmentById = Object.fromEntries(
        assignments.map((assignment) => [assignment.id, assignment]),
      );
      const allEvaluations = await fetchAllEvaluations();

      setEvaluations(
        allEvaluations
          .map((evaluation) =>
            mapEvaluationRow(
              evaluation,
              assignmentById[evaluation.assessmentAssignmentID],
            ),
          )
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
      );
    } catch (error) {
      if (notifyOnError) {
        setNotificationMessage(t("errorMessage"));
        setOpenNotification(true);
      }
      console.error(error);
    }
  }

  async function invokeGenerationApi(requestBody) {
    setOpenLoading(true);
    try {
      const apiName = "EvaluationRequestApi";
      const path = "/evaluation";
      const options = { body: requestBody };

      const restOperation = await post({ apiName, path, options });

      const { body } = await restOperation.response;
      const responseText = await body.text();
      let parsed = responseText;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = responseText;
      }

      const message =
        typeof parsed === "string"
          ? parsed
          : parsed && parsed.message
            ? parsed.message
            : JSON.stringify(parsed);

      setNotificationMessage(t(message));
      await fetchAllVisibleEvaluations(customerAssessments, {
        notifyOnError: false,
      });
    } catch (e) {
      console.error(e);
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
    setOpenLoading(false);
  }

  async function fetchAssessmentsDataTable(customers) {
    const customerAssessmentsNested = await Promise.all(
      customers.map(async (c) => {
        const assessmentAssignments =
          await fetchAllAssessmentAssignmentsByCustomer(c.id);
        const customerAssessmentAssigments = await Promise.all(
          assessmentAssignments.map(async (assignment) => {
            const assessment = await fetchAssessmentById(
              assignment.assessmentID,
            );
            const performAssessment =
              await fetchPerformedAssessmentByAssessmentAssignment(
                assignment.id,
              );
            let state = "NEW";
            let performedAssessmentID = null;
            if (
              performAssessment &&
              Array.isArray(performAssessment.items) &&
              performAssessment.items.length > 0
            ) {
              state = performAssessment.items[0].assessmentState;
              performedAssessmentID = performAssessment.items[0].id;
            }

            return {
              id: assignment.id,
              performAssessmentID: performedAssessmentID,
              customerID: c.id,
              customerName: c.name,
              assessmentID: assessment?.id ?? "",
              assessmentName:
                (assessment?.name ?? "") + " (" + assignment.package + ")",
              language: assessment?.language ?? "",
              state,
              package: assignment.package,
              type: assessment?.type ?? "",
              version: assignment.version,
            };
          }),
        );
        return customerAssessmentAssigments;
      }),
    );

    const flat = customerAssessmentsNested.flat();
    setCustomerAssessments(flat);
    await fetchAllVisibleEvaluations(flat, { notifyOnError: false });
  }

  async function fetchCustomerAndAssessments() {
    setOpenLoading(true);
    try {
      let customers = [];
      if (userGroups.includes(congnitoGroups.ADMIN)) {
        customers = await fetchCustomersAll();
      } else {
        customers = await fetchCustomersByOwnerGroups(userGroups);
      }
      await fetchAssessmentsDataTable(customers);
    } catch (error) {
      console.error(error);
      setNotificationMessage("errorMessage");
      setOpenNotification(true);
    }
    setOpenLoading(false);
  }

  useEffect(() => {
    if (!ready) return;
    fetchCustomerAndAssessments();
    fetchAllVisibleEvaluations([], { notifyOnError: false });
  }, [ready]);

  useEffect(() => {
    if (!ready || customerAssessments.length === 0 || evaluations.length === 0) {
      return;
    }

    fetchAllVisibleEvaluations(customerAssessments, { notifyOnError: false });
  }, [ready, customerAssessments.length]);

  useEffect(() => {
    if (!ready || customerAssessments.length === 0) return;

    let subscription;

    const setup = async () => {
      try {
        subscription = apiClient
          .graphql({
            query: onUpdateEvaluation,
          })
          .subscribe({
            next: ({ data }) => {
              const updatedItem = data?.onUpdateEvaluation;

              console.log(updatedItem);
              fetchAllVisibleEvaluations(customerAssessments, {
                notifyOnError: false,
              });
            },
            error: (error) => {
              console.error("Subscription error:", error);

              if (error?.errors) {
                error.errors.forEach((err, i) =>
                  console.error(`GraphQLError ${i + 1}:`, err.message || err),
                );
              }
            },
          });
      } catch (err) {
        console.error("Error:", err);
      }
    };

    setup();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [ready, customerAssessments]);

  return (
    <>
      <Box sx={{ mt: 5 }}>
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              borderBottom: 1,
              borderColor: CGI_RED,
            }}
          >
            <Typography
              sx={{
                color: CGI_RED,
                fontWeight: 500,
                fontSize: 23,
              }}
            >
              {t("assessments")}
            </Typography>
          </Box>
          <DataTable
            headers={headers}
            data={rows}
            hiddenColumns={hiddenHeaders}
            handleOnRowSelectionChanged={handleOnRowSelectionChanged}
            checkboxSelection={true}
          />
        </Container>
        <Container maxWidth="xl" sx={{ marginTop: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              borderBottom: 1,
              borderColor: CGI_RED,
            }}
          >
            <Typography
              sx={{
                color: CGI_RED,
                fontWeight: 500,
                fontSize: 23,
              }}
            >
              {t("evaluations")}
            </Typography>
          </Box>
          {evaluationGroups.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                fontStyle: "italic",
                mt: 4,
              }}
            >
              <Typography sx={{ fontFamily: "Inter", fontSize: 16 }}>
                {t("noEvaluationAvailable")}
              </Typography>
            </Box>
          ) : (
            evaluationGroups.map((group) => (
              <Box key={group.assessmentAssignmentID} sx={{ mb: 4 }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: 18,
                    fontWeight: 500,
                    mb: 1,
                  }}
                >
                  {[group.customerName, group.assessmentName]
                    .filter(Boolean)
                    .join(" / ")}
                </Typography>
                <DataTable headers={evaluationColumns} data={group.rows} />
              </Box>
            ))
          )}
        </Container>
        <LoadingModal open={openLoading} />
      </Box>
      <NotificationSnackbar
        message={notificationMessage}
        open={openNotification}
        setOpen={setOpenNotification}
      />
    </>
  );
}
